import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit2, 
  FiSearch,
  FiX,
  FiCheck,
  FiPackage,
  FiEye,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiFilter,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiTag,
  FiBox,
  FiCheckCircle,
  FiMinusCircle,
  FiToggleLeft,
  FiToggleRight,
  FiSave,
  FiRefreshCw,
  FiAlertCircle,
  FiShoppingBag,
  FiLayers,
  FiDollarSign,
  FiCode // For nick_code
} from 'react-icons/fi';
import { productAPI, accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import useResponsive from '../hooks/useResponsive';

const Products = ({ setCurrentPage }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    is_stock_based: false,
    is_active: true,
    use_currency: 'MMK',
    api_code: '',
    nick_code: '' // New field
  });

  const { isMobile, isTablet, isDesktop } = useResponsive();

  // Color theme based on sidebar products (green)
  const theme = {
    primary: 'from-green-600 to-green-700',
    primaryLight: 'from-green-500 to-green-600',
    secondary: 'from-green-500 to-emerald-500',
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    ring: 'ring-green-500',
    hover: 'hover:bg-green-50 dark:hover:bg-green-900/20',
    gradient: 'from-green-600 to-emerald-600',
    lightBg: 'bg-green-50 dark:bg-green-900/20',
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    }
  };

  useEffect(() => {
    fetchUserAndProducts();
  }, []);

  const fetchUserAndProducts = async () => {
    try {
      const userResponse = await accountAPI.getMe();
      if (userResponse.data.success) {
        const user = userResponse.data.data;
        setCurrentUser(user);
        setUserRole(user.role);
      }

      const productsResponse = await productAPI.list();
      if (productsResponse.data && productsResponse.data.success) {
        setProducts(productsResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const canCreate = () => {
    return ['system', 'super_admin'].includes(userRole);
  };

  const canEdit = (product) => {
    if (userRole === 'system') return true;
    if (userRole === 'super_admin') {
      return product.admin_id === currentUser?.id;
    }
    return false;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.code) {
      errors.code = 'Product code is required';
    } else {
      const codeRegex = /^[a-z]+$/;
      if (!codeRegex.test(formData.code)) {
        errors.code = 'Only lowercase English letters (a-z) allowed';
      }
    }

    if (!formData.name) {
      errors.name = 'Product name is required';
    } else if (formData.name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    // If not stock-based, currency, api_code, and nick_code are required
    if (!formData.is_stock_based) {
      if (!formData.use_currency) {
        errors.use_currency = 'Currency is required for non-stock products';
      }
      if (!formData.api_code) {
        errors.api_code = 'API Code is required for non-stock products';
      }
      if (!formData.nick_code) {
        errors.nick_code = 'Nick Code is required for non-stock products';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create products');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const createData = {
        code: formData.code,
        name: formData.name,
        is_stock_based: userRole === 'super_admin' ? true : formData.is_stock_based
      };

      // Only include currency, api_code, and nick_code if not stock-based
      if (!formData.is_stock_based) {
        createData.use_currency = formData.use_currency;
        createData.api_code = formData.api_code;
        createData.nick_code = formData.nick_code;
      } else {
        // Set to null for stock-based products
        createData.use_currency = null;
        createData.api_code = null;
        createData.nick_code = null;
      }

      const response = await productAPI.create(createData);
      
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Product created successfully');
        setShowModal(false);
        resetForm();
        fetchUserAndProducts();
      } else {
        toast.error(response.data?.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.message || 'Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProduct) {
      toast.error('No product selected');
      return;
    }

    if (!canEdit(selectedProduct)) {
      toast.error('You do not have permission to edit this product');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    const updateData = {};

    if (formData.code && formData.code !== selectedProduct.code) {
      updateData.code = formData.code;
    }
    
    if (formData.name && formData.name !== selectedProduct.name) {
      updateData.name = formData.name;
    }
    
    if (userRole === 'system' && formData.is_stock_based !== (selectedProduct.is_stock_based === 1)) {
      updateData.is_stock_based = formData.is_stock_based;
    }

    if (formData.is_active !== (selectedProduct.is_active === 1)) {
      updateData.is_active = formData.is_active;
    }

    // Handle currency, api_code, and nick_code based on stock_based status
    if (!formData.is_stock_based) {
      if (formData.use_currency && formData.use_currency !== selectedProduct.use_currency) {
        updateData.use_currency = formData.use_currency;
      }
      if (formData.api_code && formData.api_code !== selectedProduct.api_code) {
        updateData.api_code = formData.api_code;
      }
      if (formData.nick_code && formData.nick_code !== selectedProduct.nick_code) {
        updateData.nick_code = formData.nick_code;
      }
    } else {
      // If stock-based, set to null
      if (selectedProduct.use_currency !== null) {
        updateData.use_currency = null;
      }
      if (selectedProduct.api_code !== null) {
        updateData.api_code = null;
      }
      if (selectedProduct.nick_code !== null) {
        updateData.nick_code = null;
      }
    }

    if (Object.keys(updateData).length === 0) {
      toast.error('No changes to update');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await productAPI.update(selectedProduct.id, updateData);
      
      if (result && result.success === true) {
        toast.success(result.message || 'Product updated successfully');
        setShowModal(false);
        resetForm();
        fetchUserAndProducts();
      } else {
        const errorMsg = result?.message || 'Failed to update product';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error updating product:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update product';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    const lowercaseValue = value.toLowerCase().replace(/[^a-z]/g, '');
    setFormData({ ...formData, code: lowercaseValue });
    if (formErrors.code) {
      setFormErrors({ ...formErrors, code: null });
    }
  };

  const handleNickCodeChange = (e) => {
    const value = e.target.value;
    const lowercaseValue = value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    setFormData({ ...formData, nick_code: lowercaseValue });
    if (formErrors.nick_code) {
      setFormErrors({ ...formErrors, nick_code: null });
    }
  };

  const openEditModal = (product) => {
    if (!canEdit(product)) {
      toast.error('You do not have permission to edit this product');
      return;
    }

    setSelectedProduct(product);
    setFormData({
      code: product.code,
      name: product.name,
      is_stock_based: product.is_stock_based === 1,
      is_active: product.is_active === 1,
      use_currency: product.use_currency || 'MMK',
      api_code: product.api_code || '',
      nick_code: product.nick_code || ''
    });
    setModalType('edit');
    setShowModal(true);
    setFormErrors({});
  };

  const openCreateModal = () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create products');
      return;
    }

    resetForm();
    if (userRole === 'super_admin') {
      setFormData(prev => ({ ...prev, is_stock_based: true }));
    }
    setModalType('create');
    setShowModal(true);
    setFormErrors({});
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      is_stock_based: userRole === 'super_admin' ? true : false,
      is_active: true,
      use_currency: 'MMK',
      api_code: '',
      nick_code: ''
    });
    setSelectedProduct(null);
    setFormErrors({});
  };

  const getSortedItems = () => {
    let filtered = products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (product.nick_code && product.nick_code.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'active' && product.is_active === 1) ||
                           (statusFilter === 'inactive' && product.is_active === 0);
      
      return matchesSearch && matchesStatus;
    });

    switch(sortBy) {
      case 'newest':
        return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'name':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'code':
        return filtered.sort((a, b) => a.code.localeCompare(b.code));
      default:
        return filtered;
    }
  };

  const displayedProducts = getSortedItems();

  const totalProducts = displayedProducts.length;
  const activeProducts = displayedProducts.filter(p => p.is_active === 1).length;
  const stockBasedProducts = displayedProducts.filter(p => p.is_stock_based === 1).length;
  const nonStockProducts = displayedProducts.filter(p => p.is_stock_based === 0).length;

  const StatusBadge = ({ isActive }) => {
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
        isActive
          ? theme.badge.success
          : theme.badge.error
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const StockBadge = ({ isStockBased }) => {
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
        isStockBased
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
      }`}>
        {isStockBased ? 'Stock Based' : 'Not Stock'}
      </span>
    );
  };

  const ProductGridCard = ({ product }) => {
    const canEditThis = canEdit(product);
    
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ y: -4 }}
        className="group bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-10 h-10 rounded-xl ${theme.bg} flex items-center justify-center`}>
              <FiShoppingBag className={`w-5 h-5 ${theme.text}`} />
            </div>
            <span className="text-xs text-gray-400">ID: {product.id}</span>
          </div>

          <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1 line-clamp-1">
            {product.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
            <FiTag className="w-3 h-3" />
            {product.code}
          </p>

          <div className="flex flex-wrap gap-2 mt-auto">
            <StatusBadge isActive={product.is_active === 1} />
            <StockBadge isStockBased={product.is_stock_based === 1} />
            {product.is_stock_based === 0 && (
              <>
                <span className="px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-medium">
                  {product.use_currency || 'MMK'}
                </span>
                <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-medium">
                  {product.api_code || '-'}
                </span>
                <span className="px-3 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-lg text-xs font-medium">
                  {product.nick_code || '-'}
                </span>
              </>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <FiCalendar className="w-3 h-3" />
                {new Date(product.created_at).toLocaleDateString()}
              </div>
              <div className="flex gap-1">
                {/* Fields button - only show for system */}
                {userRole === 'system' && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setCurrentPage('stock-management', {
                        productId: product.id
                      });
                    }}
                    className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-xl transition-colors"
                    title="Manage Fields"
                  >
                    <FiLayers className="w-4 h-4" />
                  </motion.button>
                )}
                
                {canEditThis && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => openEditModal(product)}
                    className={`p-2 ${theme.hover} rounded-xl transition-colors ${theme.text}`}
                    title="Edit product"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </motion.button>
                )}
                {!canEditThis && (
                  <div className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Read only access">
                    <FiEye className="w-4 h-4 opacity-50" />
                  </div>
                )}
              </div>
            </div>
          </div>
      </motion.div>
    );
  };

  const ProductTableRow = ({ product }) => {
    const canEditThis = canEdit(product);
    
    return (
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {product.id}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {product.code}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {product.name}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <StatusBadge isActive={product.is_active === 1} />
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <StockBadge isStockBased={product.is_stock_based === 1} />
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {product.is_stock_based === 0 ? (product.use_currency || 'MMK') : '-'}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {product.is_stock_based === 0 ? (product.api_code || '-') : '-'}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {product.is_stock_based === 0 ? (product.nick_code || '-') : '-'}
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <FiCalendar className="w-3 h-3" />
            {new Date(product.created_at).toLocaleDateString()}
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
          <div className="flex items-center gap-2">
            {/* Fields button - only show for system */}
            {userRole === 'system' && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setCurrentPage('stock-management', {
                    productId: product.id
                  });
                }}
                className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                title="Manage Fields"
              >
                <FiLayers className="w-4 h-4" />
              </motion.button>
            )}
            
            {canEditThis && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openEditModal(product)}
                className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                title="Edit product"
              >
                <FiEdit2 className="w-4 h-4" />
              </motion.button>
            )}
            {!canEditThis && (
              <div className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Read only access">
                <FiEye className="w-4 h-4 opacity-50" />
              </div>
            )}
          </div>
        </td>
      </motion.tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-green-200 dark:border-green-900 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiShoppingBag className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Products
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-green-100 text-sm mt-1"
            >
              Manage your products and inventory
            </motion.p>
          </div>
        </div>

        {/* User Role Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-4 inline-flex"
        >
          <span className={`px-4 py-2 rounded-xl text-xs font-medium bg-white/20 backdrop-blur-sm text-white`}>
            {userRole === 'system' ? 'System Administrator' :
             userRole === 'super_admin' ? 'Super Admin' :
             userRole === 'reseller' ? 'Reseller' : 'Unknown'}
          </span>
        </motion.div>
      </motion.div>

      {/* Statistics Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {[
          { label: 'Total Products', value: totalProducts, icon: FiPackage, color: 'green' },
          { label: 'Active', value: activeProducts, icon: FiTrendingUp, color: 'green' },
          { label: 'Stock Based', value: stockBasedProducts, icon: FiBox, color: 'blue' },
          { label: 'Not Stock', value: nonStockProducts, icon: FiLayers, color: 'purple' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
              </div>
              <div className={`w-10 h-10 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Search and Actions Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search products by name, code or nick code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="name">Name</option>
              <option value="code">Code</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 border rounded-xl flex items-center gap-2 transition-colors ${
                showFilters 
                  ? 'bg-green-600 text-white border-green-600' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-green-300'
              }`}
            >
              <FiFilter className="w-5 h-5" />
              <span className="hidden sm:inline">Filter</span>
            </button>

            {!isMobile && (
              <div className="hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'table' 
                      ? 'bg-white dark:bg-gray-600 text-green-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                  }`}
                  title="Table view"
                >
                  <FiList className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-white dark:bg-gray-600 text-green-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                  }`}
                  title="Grid view"
                >
                  <FiGrid className="w-5 h-5" />
                </button>
              </div>
            )}

            {canCreate() && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={openCreateModal}
                className={`px-4 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg shadow-green-500/20 flex items-center gap-2 whitespace-nowrap`}
              >
                <FiPlus className="w-5 h-5" />
                <span className="hidden sm:inline">Add Product</span>
              </motion.button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
            >
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                <div className="flex gap-2">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ].map((status) => (
                    <motion.button
                      key={status.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setStatusFilter(status.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        statusFilter === status.value
                          ? status.value === 'active' 
                            ? 'bg-green-600 text-white'
                            : status.value === 'inactive'
                            ? 'bg-red-600 text-white'
                            : 'bg-green-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {status.label}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Products Display */}
      {displayedProducts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700"
        >
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-full flex items-center justify-center mb-4">
            <FiPackage className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Products Found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {searchTerm || statusFilter !== 'all' || sortBy !== 'newest'
              ? 'No products match your filters.' 
              : 'Get started by creating your first product.'}
          </p>
          {canCreate() && !searchTerm && statusFilter === 'all' && sortBy === 'newest' && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={openCreateModal}
              className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-colors text-sm shadow-lg shadow-green-500/20`}
            >
              <FiPlus className="mr-2 w-4 h-4" />
              Add your first product
            </motion.button>
          )}
        </motion.div>
      ) : (
        <>
          {isMobile ? (
            <div className="grid grid-cols-1 gap-4">
              <AnimatePresence>
                {displayedProducts.map((product) => (
                  <ProductGridCard key={product.id} product={product} />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <AnimatePresence>
                    {displayedProducts.map((product) => (
                      <ProductGridCard key={product.id} product={product} />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                  <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full min-w-[1100px]">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stock</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Currency</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">API Code</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nick Code</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        <AnimatePresence>
                          {displayedProducts.map((product) => (
                            <ProductTableRow key={product.id} product={product} />
                          ))}
                        </AnimatePresence>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                      >
                        {modalType === 'create' ? (
                          <FiPlus className="w-5 h-5 text-white" />
                        ) : (
                          <FiEdit2 className="w-5 h-5 text-white" />
                        )}
                      </motion.div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {modalType === 'create' ? 'Create Product' : 'Edit Product'}
                        </h3>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <div className="space-y-5">
                    {/* Code Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Product Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={handleCodeChange}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-green-500 focus:border-transparent
                          transition-all duration-200 lowercase
                          ${formErrors.code 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                          }
                        `}
                        placeholder="e.g., product01"
                      />
                      {formErrors.code && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.code}
                        </motion.p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Only lowercase English letters (a-z) allowed
                      </p>
                    </div>

                    {/* Name Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={`
                          w-full px-4 py-3 bg-white dark:bg-gray-800 
                          border-2 rounded-xl text-sm text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-green-500 focus:border-transparent
                          transition-all duration-200
                          ${formErrors.name 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                          }
                        `}
                        placeholder="Enter product name"
                      />
                      {formErrors.name && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.name}
                        </motion.p>
                      )}
                    </div>

                    {/* Stock Based Option */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Stock Based
                      </label>
                      
                      {userRole === 'super_admin' && modalType === 'create' && (
                        <div className={`p-4 ${theme.lightBg} rounded-xl border ${theme.border}`}>
                          <p className={`text-sm ${theme.text} mb-2`}>
                            Auto-enabled for Super Admin
                          </p>
                          <span className={`px-3 py-1.5 ${theme.bg} ${theme.text} rounded-lg text-xs font-medium`}>
                            Yes (Auto)
                          </span>
                        </div>
                      )}

                      {userRole === 'super_admin' && modalType === 'edit' && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            Cannot be changed
                          </p>
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                            formData.is_stock_based
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {formData.is_stock_based ? 'Yes' : 'No'}
                          </span>
                        </div>
                      )}

                      {userRole === 'system' && (
                        <div className="flex gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={formData.is_stock_based === true}
                              onChange={() => {
                                setFormData({ 
                                  ...formData, 
                                  is_stock_based: true,
                                  // Clear required fields when stock-based
                                  use_currency: 'MMK',
                                  api_code: '',
                                  nick_code: ''
                                });
                              }}
                              className="w-4 h-4 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Yes</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={formData.is_stock_based === false}
                              onChange={() => setFormData({ ...formData, is_stock_based: false })}
                              className="w-4 h-4 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">No</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Conditional Fields - Only show if NOT stock-based */}
                    {!formData.is_stock_based && (
                      <>
                        {/* Currency Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Currency <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.use_currency}
                            onChange={(e) => setFormData({ ...formData, use_currency: e.target.value })}
                            className={`
                              w-full px-4 py-3 bg-white dark:bg-gray-800 
                              border-2 rounded-xl text-sm text-gray-900 dark:text-white
                              focus:ring-2 focus:ring-green-500 focus:border-transparent
                              transition-all duration-200
                              ${formErrors.use_currency 
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                              }
                            `}
                          >
                            <option value="MMK">MMK</option>
                            <option value="PH">PH</option>
                            <option value="BR">BR</option>
                            <option value="USD">USD</option>
                          </select>
                          {formErrors.use_currency && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-2 text-xs text-red-500 flex items-center gap-1"
                            >
                              <FiAlertCircle className="w-3 h-3" />
                              {formErrors.use_currency}
                            </motion.p>
                          )}
                        </div>

                        {/* API Code Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            API Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.api_code}
                            onChange={(e) => setFormData({ ...formData, api_code: e.target.value.toLowerCase() })}
                            className={`
                              w-full px-4 py-3 bg-white dark:bg-gray-800 
                              border-2 rounded-xl text-sm text-gray-900 dark:text-white
                              focus:ring-2 focus:ring-green-500 focus:border-transparent
                              transition-all duration-200
                              ${formErrors.api_code 
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                              }
                            `}
                            placeholder="e.g., mobilelegends, magicchess"
                          />
                          {formErrors.api_code && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-2 text-xs text-red-500 flex items-center gap-1"
                            >
                              <FiAlertCircle className="w-3 h-3" />
                              {formErrors.api_code}
                            </motion.p>
                          )}
                          <p className="mt-2 text-xs text-gray-500">
                            API identifier for external integrations
                          </p>
                        </div>

                        {/* Nick Code Field - New */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nick Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.nick_code}
                            onChange={handleNickCodeChange}
                            className={`
                              w-full px-4 py-3 bg-white dark:bg-gray-800 
                              border-2 rounded-xl text-sm text-gray-900 dark:text-white
                              focus:ring-2 focus:ring-green-500 focus:border-transparent
                              transition-all duration-200
                              ${formErrors.nick_code 
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                              }
                            `}
                            placeholder="e.g., mobile-legends, mcgg"
                          />
                          {formErrors.nick_code && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-2 text-xs text-red-500 flex items-center gap-1"
                            >
                              <FiAlertCircle className="w-3 h-3" />
                              {formErrors.nick_code}
                            </motion.p>
                          )}
                          <p className="mt-2 text-xs text-gray-500">
                            Nickname code - lowercase letters, numbers, hyphens, underscores only
                          </p>
                        </div>
                      </>
                    )}

                    {/* Status Toggle - Only in Edit Mode */}
                    {modalType === 'edit' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Product Status
                        </label>
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Current Status:
                          </span>
                          <button
                            onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                            className={`relative inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              formData.is_active
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full mr-2 bg-white" />
                            {formData.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </div>
                      </div>
                    )}

                    {modalType === 'edit' && selectedProduct && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Created: {new Date(selectedProduct.created_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t-2 border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowModal(false)}
                      className="px-6 py-3 rounded-xl font-medium text-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </motion.button>
                    
                    {(modalType === 'create' && canCreate()) || 
                     (modalType === 'edit' && canEdit(selectedProduct)) ? (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={modalType === 'create' ? handleCreate : handleUpdate}
                        disabled={isSubmitting}
                        className={`px-8 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-green-700 hover:to-green-800 transition-all shadow-lg shadow-green-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isSubmitting ? (
                          <>
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <FiSave className="w-4 h-4" />
                            <span>{modalType === 'create' ? 'Create' : 'Update'}</span>
                          </>
                        )}
                      </motion.button>
                    ) : null}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default Products;